import React, { useCallback, useEffect, useState } from "react";
import { Typography, Box, Avatar, Tooltip, Chip } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import { useProjectActivityViewModel } from "./ProjectActivityViewModel";
import Modal from "../../../components/Modal/Modal";
import { useRbac } from "../../../hooks/RbacContext";
import { useTheme } from "@mui/material/styles";
import formFields from "../../../config/formFields.json";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Popover from "@mui/material/Popover";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PercentIcon from "@mui/icons-material/Percent";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { getEnumLabel, getEnumOptions } from "../../../utils/enumUtils";

const ProjectActivity = ({ projectId }) => {
  const {
    tasks,
    loading,
    error,
    successModal,
    resources,
    setSuccessModal,
    getResourceName,
    fetchTasks,
    handleRowSave,
    setSelectedTask,
    addNewRow,
    handleNewRowSave,
    editingRow,
    setEditingRow,
    handleCancelNew,
    isNewRow,
    unavailableResourceIds,
    handleApproveClick,
    approvalConfirmation,
    resourceConfirmation,
    handleDeleteTask,
    deleteConfirmation,
    projects,
    projectDetails,
  } = useProjectActivityViewModel(projectId);
  console.log("projectDetails", projectDetails);
  const { hasProjectPermission, checkAccess } = useRbac();
  const theme = useTheme();

  const hasTaskEditAccess =
    hasProjectPermission(parseInt(projectId), "tasks", "update") ||
    checkAccess("projectTasks", "update");
  const hasTaskApproveAccess =
    hasProjectPermission(parseInt(projectId), "tasks", "approve") ||
    checkAccess("projectTasks", "approve");

  const taskFormFields = formFields.task.basic.fields;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedResource, setSelectedResource] = React.useState(null);
  const [dateFilterOpen, setDateFilterOpen] = useState(null);
  const [dateRangeValue, setDateRangeValue] = useState([null, null]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [showAddButton, setShowAddButton] = useState(true);
  const [projectPublish, setProjectPublish] = useState(false);

  useEffect(() => {
    // const projectDetail = projectDetails?.filter(
    //   (project) => project.project_id === projectId
    // );
    const projectMilestoneDetails = tasks.filter(
      (milestone) => milestone.publish_type === "PUBLISHED"
    );
    if (
      projectMilestoneDetails?.length > 0 &&
      projectDetails &&
      projectDetails?.publish_type === "APPROVED"
    ) {
      setShowAddButton(false);
    }
    setProjectPublish(
      projectDetails && projectDetails?.publish_type === "APPROVED"
    );
    setFilteredTasks(tasks);
  }, [tasks, projectDetails]);

  const columns = [
    {
      id: "milestone_code",
      label: "Code",
      searchValue: (value, row) => {
        const milestoneCode = row?.milestone_code || value;
        return milestoneCode
          ? milestoneCode.toString().toLowerCase().trim()
          : "";
      },
      type: "text",
      formField: taskFormFields.milestone_code,
      width: 120,
      render: (value) => (
        <Box
          sx={{
            maxWidth: "120px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "0.8rem",
          }}
        >
          {value || ""}
        </Box>
      ),
      sx: {
        width: "120px",
        minWidth: "120px",
        maxWidth: "120px",
      },
    },
    {
      id: "task_name",
      label: "Milestone Name",
      searchValue: (value, row) => {
        const searchStr = row?.task_name || value || "";
        return searchStr.toString().toLowerCase().trim();
      },
      type: "text",
      formField: taskFormFields.task_name,
      width: 180,
      render: (value) => (
        <Tooltip title={value || ""} arrow placement="top">
          <Box
            sx={{
              maxWidth: "180px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
              fontSize: "0.8rem",
            }}
          >
            {value}
          </Box>
        </Tooltip>
      ),
      sx: {
        width: "180px",
        minWidth: "180px",
        maxWidth: "180px",
      },
    },
    {
      id: "estimated_hours",
      label: "Est. Hours",
      searchValue: (value, row) => {
        const hours = row?.estimated_hours || value;
        return hours ? hours.toString().toLowerCase().trim() : "";
      },
      type: "number",
      formField: taskFormFields.estimated_hours,
    },
    {
      id: "start_date",
      label: "Start Date",
      searchValue: (value, row) => {
        const date = row?.start_date || value;
        if (!date) return "";
        try {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let dateObj;
          if (typeof date === "number" || /^\d{13,}$/.test(String(date))) {
            const timestamp = parseInt(date);
            // Validate timestamp range (reasonable date range)
            if (timestamp > 0 && timestamp < 9999999999999) {
              dateObj = new Date(timestamp);
            } else {
              console.warn("Invalid timestamp value:", date);
              return "";
            }
          } else {
            dateObj = new Date(date);
          }

          // Check if the date is valid
          if (isNaN(dateObj.getTime())) {
            console.warn("Invalid date value:", date);
            return "";
          }

          return dateObj
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            .toLowerCase();
        } catch (e) {
          console.warn("Error converting date:", date, e);
          return "";
        }
      },
      sortValue: (value, row) => {
        if (!row.start_date) return null;
        try {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let date;
          if (
            typeof row.start_date === "number" ||
            /^\d{13,}$/.test(String(row.start_date))
          ) {
            const timestamp = parseInt(row.start_date);
            // Validate timestamp range (reasonable date range)
            if (timestamp > 0 && timestamp < 9999999999999) {
              date = new Date(timestamp);
            } else {
              console.warn("Invalid timestamp value:", row.start_date);
              return null;
            }
          } else {
            date = new Date(row.start_date);
          }

          // Check if the date is valid
          if (isNaN(date.getTime())) {
            console.warn("Invalid date value:", row.start_date);
            return null;
          }

          return new Date(
            Date.UTC(
              date.getUTCFullYear(),
              date.getUTCMonth(),
              date.getUTCDate(),
              12,
              0,
              0,
              0
            )
          );
        } catch (e) {
          console.warn("Error converting date:", row.start_date, e);
          return null;
        }
      },
      type: "date",
      formField: taskFormFields.start_date,
      render: (value) => {
        if (!value) return "";
        try {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let dateObj;
          if (typeof value === "number" || /^\d{13,}$/.test(String(value))) {
            const timestamp = parseInt(value);
            // Validate timestamp range (reasonable date range)
            if (timestamp > 0 && timestamp < 9999999999999) {
              dateObj = new Date(timestamp);
            } else {
              console.warn("Invalid timestamp value:", value);
              return "Invalid Date";
            }
          } else {
            dateObj = new Date(value);
          }

          // Check if the date is valid
          if (isNaN(dateObj.getTime())) {
            console.warn("Invalid date value:", value);
            return "Invalid Date";
          }

          return dateObj.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        } catch (e) {
          console.warn("Error converting date:", value, e);
          return "Invalid Date";
        }
      },
    },
    {
      id: "end_date",
      label: "End Date",
      searchValue: (value, row) => {
        const date = row?.end_date || value;
        if (!date) return "";
        try {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let dateObj;
          if (typeof date === "number" || /^\d{13,}$/.test(String(date))) {
            const timestamp = parseInt(date);
            // Validate timestamp range (reasonable date range)
            if (timestamp > 0 && timestamp < 9999999999999) {
              dateObj = new Date(timestamp);
            } else {
              console.warn("Invalid timestamp value:", date);
              return "";
            }
          } else {
            dateObj = new Date(date);
          }

          // Check if the date is valid
          if (isNaN(dateObj.getTime())) {
            console.warn("Invalid date value:", date);
            return "";
          }

          return dateObj
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            .toLowerCase();
        } catch (e) {
          console.warn("Error converting date:", date, e);
          return "";
        }
      },
      sortValue: (value, row) => {
        if (!row.end_date) return null;
        try {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let date;
          if (
            typeof row.end_date === "number" ||
            /^\d{13,}$/.test(String(row.end_date))
          ) {
            const timestamp = parseInt(row.end_date);
            // Validate timestamp range (reasonable date range)
            if (timestamp > 0 && timestamp < 9999999999999) {
              date = new Date(timestamp);
            } else {
              console.warn("Invalid timestamp value:", row.end_date);
              return null;
            }
          } else {
            date = new Date(row.end_date);
          }

          // Check if the date is valid
          if (isNaN(date.getTime())) {
            console.warn("Invalid date value:", row.end_date);
            return null;
          }

          return new Date(
            Date.UTC(
              date.getUTCFullYear(),
              date.getUTCMonth(),
              date.getUTCDate(),
              12,
              0,
              0,
              0
            )
          );
        } catch (e) {
          console.warn("Error converting date:", row.end_date, e);
          return null;
        }
      },
      type: "date",
      formField: taskFormFields.end_date,
      render: (value) => {
        if (!value) return "";
        try {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let dateObj;
          if (typeof value === "number" || /^\d{13,}$/.test(String(value))) {
            const timestamp = parseInt(value);
            // Validate timestamp range (reasonable date range)
            if (timestamp > 0 && timestamp < 9999999999999) {
              dateObj = new Date(timestamp);
            } else {
              console.warn("Invalid timestamp value:", value);
              return "Invalid Date";
            }
          } else {
            dateObj = new Date(value);
          }

          // Check if the date is valid
          if (isNaN(dateObj.getTime())) {
            console.warn("Invalid date value:", value);
            return "Invalid Date";
          }

          return dateObj.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        } catch (e) {
          console.warn("Error converting date:", value, e);
          return "Invalid Date";
        }
      },
    },
    {
      id: "resource_mappings",
      label: "Members | Allocate Hours",
      searchValue: (value, row) => {
        const mappings = row?.resource_mappings || value;
        if (!Array.isArray(mappings)) return "";
        return mappings
          .map((r) => {
            const resourceName = getResourceName(r.resource_id);
            return resourceName
              ? resourceName.toString().toLowerCase().trim()
              : "";
          })
          .filter(Boolean)
          .join(" ");
      },
      icon: <PersonIcon />,
      type: "resource_allocation",
      formField: taskFormFields.resource_mappings,
      render: (value, row) => {
        if (!Array.isArray(value) || row.isEditing) return "";

        const getStatusColor = (percentage) => {
          return {
            main: theme.palette.primary.main,
            light: `${theme.palette.primary.main}15`,
            gradient: `linear-gradient(135deg, ${theme.palette.primary.main}80, ${theme.palette.primary.main})`,
          };
        };

        const handlePopoverOpen = (event, resource) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
          setSelectedResource(resource);
        };

        const handlePopoverClose = (event) => {
          event.stopPropagation();
          setAnchorEl(null);
          setSelectedResource(null);
        };

        const open = Boolean(anchorEl);

        // Calculate allocation percentage for each resource
        const calculateAllocationPercentage = (resource) => {
          const estimatedHours = parseFloat(row.estimated_hours) || 0;
          const resourceHours =
            parseFloat(
              resource.resource_budget_hours || resource.allocation_hours
            ) || 0;

          if (estimatedHours === 0) return 0;

          const percentage = (resourceHours / estimatedHours) * 100;
          return Math.round(percentage * 100) / 100; // Round to 2 decimal places
        };

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {value.map((r, index) => {
              const resourceName = getResourceName(r.resource_id);
              if (!resourceName) return null;

              // Calculate allocation percentage
              const allocationPercentage = calculateAllocationPercentage(r);
              const colors = getStatusColor(allocationPercentage);

              return (
                <React.Fragment key={`${r.resource_id}-${index}`}>
                  <Avatar
                    onClick={(e) =>
                      handlePopoverOpen(e, {
                        ...r,
                        allocation_pc: allocationPercentage,
                      })
                    }
                    className="avatar"
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: "0.75rem",
                      bgcolor: colors.light,
                      color: colors.main,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: `2px solid ${colors.main}20`,
                      "&:hover": {
                        bgcolor: colors.main,
                        color: "#fff",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    {resourceName.charAt(0).toUpperCase()}
                  </Avatar>

                  <Popover
                    open={
                      open && selectedResource?.resource_id === r.resource_id
                    }
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    sx={{
                      "& .MuiPopover-paper": {
                        boxShadow: theme.shadows[2],
                        borderRadius: "8px",
                        overflow: "hidden",
                        width: 240,
                      },
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          background: colors.gradient,
                          p: 1.5,
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "rgba(255,255,255,0.3)",
                            fontSize: "0.875rem",
                          }}
                        >
                          {resourceName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {resourceName}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: "0.75rem",
                              }}
                            >
                              <AccessTimeIcon sx={{ fontSize: "0.875rem" }} />
                              {selectedResource?.resource_budget_hours ||
                                selectedResource?.allocation_hours}
                              h
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: "0.75rem",
                              }}
                            >
                              {selectedResource?.allocation_pc ||
                                allocationPercentage}
                              %
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Popover>
                </React.Fragment>
              );
            })}
          </Box>
        );
      },
    },
    {
      id: "task_status_code",
      label: "Status",
      searchValue: (value, row) => {
        const status = row?.task_status_code || value;
        return status ? status.toString().toLowerCase().trim() : "";
      },
      type: "select",
      formField: {
        ...taskFormFields.task_status_code,
        source: "task_status_code",
        readOnly: (row) =>
          row.isNew || row.task_id?.toString().startsWith("temp-"),
        disabled: (row) =>
          row.isNew || row.task_id?.toString().startsWith("temp-"),
      },
      render: (value) => {
        const getStatusColor = (status) => {
          switch (status) {
            case "TO_DO":
              return { bg: "#f0f0f0", color: "#666", label: "PLANNED" };
            case "IN_PROGRESS":
              return { bg: "#e3f2fd", color: "#1976d2", label: "IN_PROGRESS" };
            case "COMPLETED":
              return { bg: "#e8f5e8", color: "#2e7d32", label: "COMPLETED" };
            case "ON_HOLD":
              return { bg: "#fff3e0", color: "#f57c00", label: "ON_HOLD" };
            default:
              return { bg: "#f0f0f0", color: "#666", label: "PLANNED" };
          }
        };

        const statusConfig = getStatusColor(value);
        // Use custom label for TO_DO status, otherwise use enum label
        const statusLabel =
          value === "TO_DO"
            ? "Planned"
            : getEnumLabel("task_status_code", value);

        return (
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              backgroundColor: statusConfig.bg,
              color: statusConfig.color,
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      },
      width: 120,
      sx: {
        width: "120px",
        minWidth: "120px",
        maxWidth: "120px",
      },
    },
  ];

  const columnFilters = [
    {
      id: "resource_mappings",
      type: "select",
      label: "Member",
      icon: <PersonIcon />,
      sendIcon: true,
      render: (value) => getResourceName(value[0]?.resource_id),
    },
  ];

  const fixedColumns = {
    left: [],
    right: [],
  };

  const getOptionsForField = useCallback(
    (source, isNewTask) => {
      switch (source) {
        case "resources":
          return resources.map((resource) => ({
            value: resource.resource_id.toString(),
            label: `${resource.first_name} ${resource.last_name}`,
          }));
        case "task_status_code": {
          if (isNewTask) {
            return [{ label: "Planned", value: "TO_DO" }];
          }
          const hidePlanned =
            projectDetails?.publish_type === "APPROVED";
          const existingOptions =
            formFields.task.basic.fields.task_status_code.options?.length > 0
              ? formFields.task.basic.fields.task_status_code.options
              : getEnumOptions("task_status_code");
          return hidePlanned
            ? existingOptions.filter((opt) => opt?.value !== "TO_DO")
            : existingOptions;
        }
        default:
          return [];
      }
    },
    [resources, formFields, projectDetails]
  );

  useEffect(() => {
    if (projectId) {
      localStorage.setItem("currentProjectId", projectId);
    }
  }, [projectId]);

  useEffect(() => {
    setFilteredTasks(tasks);
  }, [tasks]);

  const handleFilterDateClick = (event) => {
    setDateFilterOpen(event.currentTarget);
  };

  const handleCloseDateChangeFilter = () => {
    setDateFilterOpen(null);
  };
  const handleClearDateFilter = () => {
    setFilteredTasks(tasks);
    setDateRangeValue([null, null]);
  };

  const handleDateChangeFilter = (dates) => {
    setDateRangeValue(dates);
    onFilter(dates);
  };

  const onFilter = ([start, end]) => {
    if (!start || !end) {
      setFilteredTasks(tasks);
      return;
    }

    const startDateStr = start.toISOString().split("T")[0];
    const endDateStr = end.toISOString().split("T")[0];

    const filteredData = tasks.filter((row) => {
      const taskStart = row.start_date ? row.start_date.split("T")[0] : null;
      const taskEnd = row.end_date ? row.end_date.split("T")[0] : null;

      if (!taskStart || !taskEnd) return true;

      return (
        (taskStart >= startDateStr && taskStart <= endDateStr) ||
        (taskEnd >= startDateStr && taskEnd <= endDateStr) ||
        (taskStart <= startDateStr && taskEnd >= endDateStr)
      );
    });

    setFilteredTasks(filteredData);
  };

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }
  return (
    <div>
      <CustomTable
        columns={columns}
        data={filteredTasks}
        loading={loading}
        onAdd={addNewRow}
        showActions={true}
        showAddButton={
          hasProjectPermission(parseInt(projectId), "tasks", "add") ||
          checkAccess("projectTasks", "add")
        }
        addButtonDisable={!showAddButton}
        addButtonIcon={<AddIcon />}
        moduleName="task"
        formFields={taskFormFields}
        getOptionsForField={getOptionsForField}
        onRowSave={(formData) => {
          if (
            formData.isNew ||
            formData.task_id?.toString().startsWith("temp-")
          ) {
            return handleNewRowSave(formData);
          }
          return handleRowSave(formData);
        }}
        showSearch={true}
        showColumnVisibility={false}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        title="Project Milestones "
        subtitle="Manage and monitor project Milestones"
        addButtonText="Add Milestone"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
        setSelectedTask={setSelectedTask}
        rowEdit={true}
        editingRow={editingRow}
        setEditingRow={setEditingRow}
        onRefresh={fetchTasks}
        onCancelNew={(updatedTasks) => handleCancelNew(updatedTasks)}
        isNewRow={isNewRow}
        unavailableResourceIds={unavailableResourceIds}
        showDateFilter={true}
        dateFilterOpen={dateFilterOpen}
        dateRangeValue={dateRangeValue}
        handleCloseDateChangeFilter={handleCloseDateChangeFilter}
        handleFilterDateClick={handleFilterDateClick}
        handleDateChangeFilter={handleDateChangeFilter}
        handleClearDateFilter={handleClearDateFilter}
        searchPlaceholder="Search tasks..."
        onStatusUpdate={handleApproveClick}
        showStatusUpdate={true}
        hasEditAccess={hasTaskEditAccess}
        hasApproveAccess={hasTaskApproveAccess}
        onDelete={handleDeleteTask}
        projectDetails={projectDetails}
        publishMilestoneButton={projectPublish}
        setAddButtonDisable={setShowAddButton}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={() => setSuccessModal(null)}
        />
      )}

      {approvalConfirmation && (
        <ConfirmationModal
          open={true}
          title="Confirm Approval"
          message={approvalConfirmation.message}
          onClose={approvalConfirmation.onCancel}
          onConfirm={approvalConfirmation.onConfirm}
          buttonText="Approve"
          colorCode="#2E7D32"
        />
      )}

      {resourceConfirmation && (
        <ConfirmationModal
          open={true}
          title="Resource Unavailable"
          message={resourceConfirmation.message}
          onClose={resourceConfirmation.onCancel}
          onConfirm={resourceConfirmation.onConfirm}
          buttonText={resourceConfirmation.buttonText}
          colorCode={resourceConfirmation.colorCode}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title={deleteConfirmation.title}
          message={deleteConfirmation.message}
          onClose={deleteConfirmation.onCancel}
          onConfirm={deleteConfirmation.onConfirm}
          buttonText="Delete"
          colorCode="#d32f2f"
        />
      )}
    </div>
  );
};

export default ProjectActivity;
